import { useMemo, useState, useCallback, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { FGTrackingTabs, FGTrackingTable, Filter } from "../components";

import {
  getVmIdsBySellerNames,
  getVmIdsByStates,
  normalizeFgTrackingData,
} from "../makeData";
import { postCall_v2, getCall } from "services";
import { useToaster } from "hooks";
import { formatDateToISO } from "utils";

import { error } from "constants";
import {
  FG_TRACKING_TABS,
  FG_TRACKING_TITLE,
  fgReadinessPayloadMap,
  orderTypesPayloadMap,
} from "../constants";

import FGRefreshContext from "../FGRefreshContext";

const TAB_COUNT_KEY_BY_VALUE = {
  allOrders: "all",
  dueDatePending: "fgpend",
  paymentPending: "pypend",
  doPending: "dopend",
  doCreated: "docr",
  exceptions: "exceptions",
};

const SORT_FIELD_MAP = {
  ageing: "aging",
  fgReadyBy: "fgDays",
};

const FGTracking = () => {
  const [sellerData, setSellerData] = useState([]);
  const [customersData, setCustomersData] = useState([]);
  const [buyerLoading, setBuyerLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(FG_TRACKING_TABS[0].value);
  const [expandedRows, setExpandedRows] = useState([]);
  const [selectedLineItemsByOrder, setSelectedLineItemsByOrder] = useState({});
  const [fgTrackingData, setFgTrackingData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    sortBy: "aging",
    sortDir: "ASC",
  });
  const paginationData = fgTrackingData.pagination || {};
  const tabCountsData = fgTrackingData.tabCounts || {};
  const [page, setPage] = useState(
    Math.max(0, Number(paginationData.currentPage || 1) - 1),
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(paginationData.pageSize || 20),
  );

  const triggerToaster = useToaster();

  const fetchPartyData = useCallback(
    async (input, { setData, setLoading, label }) => {
      if (!input) {
        setData([]);
        return;
      }
      try {
        setLoading(true);
        setData([]);
        const res = await getCall(
          `/joms/api/external/v1/customer-details-by-reference/${input}`,
        );
        setData([...new Set(res?.data?.map((item) => item.companyName))] ?? []);
      } catch (err) {
        console.error(`${label} API failed`, err);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchCustomerData = useCallback(
    (customerInput) =>
      fetchPartyData(customerInput, {
        setData: setCustomersData,
        setLoading: setBuyerLoading,
        label: "Customer",
      }),
    [fetchPartyData],
  );

  const handleSortChange = useCallback((field) => {
    const apiField = SORT_FIELD_MAP[field];

    if (!apiField) {
      return;
    }

    setIsLoading(true);
    setSortConfig((prev) => ({
      sortBy: apiField,
      sortDir:
        prev.sortBy === apiField && prev.sortDir === "ASC" ? "DESC" : "ASC",
    }));
    setPage(0);
  }, []);

  const handleFilterSubmit = useCallback(
    (data) => {
      const {
        dateFrom,
        dateTo,
        dateType,
        sellerNames,
        sellerStates,
        orderType,
        deliveryType,
        ...restData
      } = data;
      const sellerVmIds = getVmIdsBySellerNames(sellerData, sellerNames);
      const stateVmIds = getVmIdsByStates(sellerData, sellerStates);

      const filterData = {
        ...restData,
        dateFrom: formatDateToISO(dateFrom),
        dateTo: formatDateToISO(dateTo),
        dateType: fgReadinessPayloadMap[dateType],
        orderType: orderTypesPayloadMap[orderType],
        deliveryType: deliveryType == "ALL" ? "" : deliveryType,
        vmIds: [...new Set([...sellerVmIds, ...stateVmIds])],
      };
      setIsLoading(true);
      setFilters(filterData);
      setPage(0);
    },
    [sellerData],
  );

  const handleFilterReset = useCallback(() => {
    setIsLoading(true);
    setFilters({});
    setPage(0);
    setSortConfig({ sortBy: "aging", sortDir: "ASC" });
  }, []);

  const fetchFGTrackingData = useCallback(
    async ({
      tab,
      page: currentPage,
      pageSize,
      isBackgroundFetch,
      currentSelectedTab,
      ...activeFilters
    }) => {
      let hasApiData = false;
      let wasCancelled = false;

      setIsLoading(true);

      try {
        const result = await postCall_v2(
          `/joms/api/v2/fg-tracking/orders/search`,
          {
            tab,
            page: currentPage + 1,
            pageSize,
            ...activeFilters,
          },
          {},
          {},
          "fg-tracking-search",
        );

        if (!result) {
          wasCancelled = true;
          return;
        }

        if (result.data) {
          setFgTrackingData(
            normalizeFgTrackingData(result?.data, currentSelectedTab),
          );
          hasApiData = true;
        }
      } catch (err) {
        triggerToaster(
          err?.data?.errorMessage || "Something went wrong!",
          error,
        );
        console.error("Error fetching FG tracking:", err);
      } finally {
        if (!wasCancelled) {
          setIsLoading(false);
        }
      }
    },
    [],
  );

  const fetchData = useCallback(
    ({ isBackgroundFetch = false } = {}) => {
      const tab = TAB_COUNT_KEY_BY_VALUE[selectedTab];
      void fetchFGTrackingData({
        tab,
        page,
        pageSize: rowsPerPage,
        currentSelectedTab: selectedTab,
        ...filters,
        sortBy: sortConfig.sortBy,
        sortDir: sortConfig.sortDir,
        isBackgroundFetch,
      });
    },
    [selectedTab, page, rowsPerPage, filters, fetchFGTrackingData, sortConfig],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const tabs = useMemo(
    () =>
      FG_TRACKING_TABS.map((tab) => {
        const countKey = TAB_COUNT_KEY_BY_VALUE[tab.value];
        const apiCount = Number(tabCountsData[countKey]);
        const isSelectedTab = tab.value === selectedTab;

        const ordersSettled = !isLoading;

        const fallbackCount =
          isSelectedTab && ordersSettled
            ? (fgTrackingData?.orders || []).length
            : 0;

        const count =
          (Number.isNaN(apiCount) || apiCount === 0) && fallbackCount > 0
            ? fallbackCount
            : Number.isNaN(apiCount)
            ? 0
            : apiCount;

        return {
          ...tab,
          count,
        };
      }),
    [tabCountsData, fgTrackingData.orders, selectedTab, isLoading],
  );

  const orders = fgTrackingData.orders || [];
  const pagination = useMemo(
    () => ({
      page,
      rowsPerPage,
      totalCount: fgTrackingData.pagination?.totalRecords || 0,
    }),
    [page, rowsPerPage, fgTrackingData],
  );

  const handleTabChange = useCallback((_event, tab) => {
    setIsLoading(true);
    setSelectedTab(tab);
    setExpandedRows([]);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((_event, newPage) => {
    setIsLoading(true);
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setIsLoading(true);
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleToggleExpand = useCallback((orderId) => {
    setExpandedRows((previousRows) =>
      previousRows.includes(orderId)
        ? previousRows.filter((id) => id !== orderId)
        : [...previousRows, orderId],
    );
  }, []);

  const handleToggleLineItem = useCallback((orderId, lineItemId) => {
    setSelectedLineItemsByOrder((previousMap) => {
      const currentSelected = previousMap[orderId] || [];
      const isSelected = currentSelected.includes(lineItemId);

      return {
        ...previousMap,
        [orderId]: isSelected
          ? currentSelected.filter((id) => id !== lineItemId)
          : [...currentSelected, lineItemId],
      };
    });
  }, []);

  const fetchAllSellers = async () => {
    try {
      const res = await getCall(
        "/joms/api/internal/v1/finished-goods/fetch-fg-lineitem-sellers",
      );
      setSellerData(res?.data);
    } catch {
      setSellerData([]);
    }
  };

  const handleToggleSelectAll = useCallback(
    (orderId, allEligibleSelected) => {
      const order = orders.find((item) => item.orderId === orderId);
      const eligibleIds = (order?.lineItems || [])
        .filter((item) => item.eligible)
        .map((item) => item.id);

      setSelectedLineItemsByOrder((previousMap) => ({
        ...previousMap,
        [orderId]: allEligibleSelected ? [] : eligibleIds,
      }));
    },
    [orders],
  );

  const refreshDataInBackground = useCallback(() => {
    fetchData({ isBackgroundFetch: true });
  }, [fetchData]);

  useEffect(() => {
    void fetchAllSellers();
  }, []);

  return (
    <Box p={3}>
      <Typography variant="h4" mb={2}>
        {FG_TRACKING_TITLE}
      </Typography>

      <Filter
        sellerData={sellerData}
        customersData={customersData}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        fetchCustomersData={fetchCustomerData}
        buyerLoading={buyerLoading}
      />

      <FGTrackingTabs
        tabs={tabs}
        selectedTab={selectedTab}
        onChange={handleTabChange}
      />

      <Box mt={2}>
        <FGRefreshContext.Provider value={fetchData}>
          <FGTrackingTable
            isLoading={isLoading}
            orders={orders}
            pagination={pagination}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            expandedRows={expandedRows}
            onToggleExpand={handleToggleExpand}
            selectedLineItemsByOrder={selectedLineItemsByOrder}
            onToggleLineItem={handleToggleLineItem}
            onToggleSelectAll={handleToggleSelectAll}
            sortConfig={sortConfig}
            onSortByToggle={handleSortChange}
            onSortDirToggle={handleSortChange}
          />
        </FGRefreshContext.Provider>
      </Box>
    </Box>
  );
};

export default FGTracking;
